import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, priceLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar as CalendarBlank, Map as MapTrifold, ArrowRight, Pencil, Trash2, QrCode, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";
import TrustBadge from "@/components/TrustBadge";
import TrustNudgeCard from "@/components/TrustNudgeCard";

const statusStyle = (s) => ({
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-[#E05E36]/15 text-[#E05E36]",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-stone-200 text-stone-700",
})[s] || "bg-stone-100";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ as_customer: [], as_provider: [] });
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/mine").then(r => setData(r.data));
    api.get("/listings/mine/all").then(r => setMine(r.data));
  }, [user]);

  const deleteListing = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/listings/${id}`);
      setMine(m => m.filter(l => l.listing_id !== id));
      toast.success("Listing deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="dash-login">Login with Google</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Welcome back</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Hi, {user.name?.split(" ")[0]} 👋</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/post"><Button className="rounded-full bg-[#2D5A27] hover:bg-[#24491f]">+ Post Listing</Button></Link>
          <Link to="/search"><Button variant="outline" className="rounded-full">Explore</Button></Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <StatCard label="My bookings" value={data.as_customer.length} />
        <StatCard label="Jobs received" value={data.as_provider.length} />
        <StatCard label="My listings" value={mine.length} />
      </div>

      <TrustNudgeCard />

      <Tabs defaultValue="customer" className="mt-8">
        <TabsList className="rounded-full bg-stone-100 p-1">
          <TabsTrigger value="customer" className="rounded-full data-[state=active]:bg-white" data-testid="tab-customer">My bookings</TabsTrigger>
          <TabsTrigger value="provider" className="rounded-full data-[state=active]:bg-white" data-testid="tab-provider">As provider</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full data-[state=active]:bg-white" data-testid="tab-listings">My listings</TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="mt-4">
          <BookingList items={data.as_customer} empty="No bookings yet" />
        </TabsContent>
        <TabsContent value="provider" className="mt-4">
          <BookingList items={data.as_provider} empty="No jobs received yet — publish a listing to receive requests." />
        </TabsContent>
        <TabsContent value="listings" className="mt-4">
          {mine.length === 0 ? (
            <div className="text-center py-10 text-stone-500">No listings yet. <Link to="/post" className="text-[#E05E36] font-semibold">Post one now →</Link></div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {mine.map(l => (
                <div key={l.listing_id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow relative group" data-testid={`mylisting-${l.listing_id}`}>
                  <Link to={`/listing/${l.listing_id}`} className="block">
                    <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                      {l.images?.[0] && <img loading="lazy" src={l.images[0]} className="w-full h-full object-cover" alt="" />}
                    </div>
                    <div className="p-4 pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{l.category}</div>
                        {l.owner_trust && <TrustBadge trust={l.owner_trust} size="xs" />}
                      </div>
                      <div className="heading font-bold text-stone-900 line-clamp-1 mt-1">{l.title}</div>
                      <div className="mt-1 text-[#E05E36] font-bold text-sm">{priceLabel(l.price, l.price_unit)}</div>
                    </div>
                  </Link>
                  <div className="px-4 pb-4 flex gap-2">
                    <button
                      onClick={() => navigate(`/promote?listing_id=${l.listing_id}&sku=top_listing`)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-xs font-bold py-2 shadow-sm shadow-[#E05E36]/30 transition-colors"
                      title="Promote this listing"
                      data-testid={`promote-${l.listing_id}`}
                    >
                      <Megaphone size={12} /> Promote
                    </button>
                    <button
                      onClick={() => navigate(`/edit/${l.listing_id}`)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-[#2D5A27] hover:text-[#2D5A27] text-xs font-bold text-stone-700 px-3 py-2 transition-colors"
                      title="Edit listing"
                      data-testid={`edit-${l.listing_id}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => navigate(`/listing/${l.listing_id}?qr=1`)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-[#E05E36] hover:text-[#E05E36] text-xs font-bold text-stone-700 px-3 py-2 transition-colors"
                      aria-label="Storefront QR sticker"
                      title="Print storefront QR sticker"
                      data-testid={`qr-${l.listing_id}`}
                    >
                      <QrCode size={12} />
                    </button>
                    <button
                      onClick={() => deleteListing(l.listing_id, l.title)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-stone-300 hover:border-red-500 hover:text-red-600 text-xs font-bold text-stone-700 px-3 py-2 transition-colors"
                      aria-label="Delete listing"
                      data-testid={`delete-${l.listing_id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{label}</div>
      <div className="heading text-3xl font-extrabold text-stone-900 mt-1">{value}</div>
    </div>
  );
}

function BookingList({ items, empty }) {
  if (!items || items.length === 0) return <div className="text-center py-10 text-stone-500">{empty}</div>;
  return (
    <div className="space-y-3">
      {items.map(b => (
        <div key={b.booking_id} className="bg-white border border-stone-200 rounded-2xl p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`booking-${b.booking_id}`}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#FFF3EC] text-[#E05E36] grid place-items-center"><CalendarBlank size={20} /></div>
            <div>
              <div className="heading font-bold text-stone-900">{b.listing_title}</div>
              <div className="text-xs text-stone-500">Scheduled: {new Date(b.scheduled_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[11px] font-bold px-3 py-1 rounded-full ${statusStyle(b.status)}`}>{b.status.replace("_", " ")}</span>
            <Link to={`/track/${b.booking_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#E05E36]" data-testid={`track-${b.booking_id}`}><MapTrifold size={16} /> Track <ArrowRight size={14} /></Link>
          </div>
        </div>
      ))}
    </div>
  );
}
